// ============================================================================
// MODULE DATING - LAYOUT PRINCIPAL
// Layout avec vérification d'abonnement et navigation
// ============================================================================

import React, { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import {
  Heart,
  MessageCircle,
  User,
  Crown,
  Settings,
  LogOut,
  Sparkles,
  Users,
  Bell,
  Menu,
  X,
  ChevronLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DatingProfile,
  DatingSubscription,
  DatingAuthContext,
  DatingDailyLimits
} from '../../types/dating';
import {
  getCurrentDatingProfile,
  getActiveDatingSubscription,
  getDatingDailyLimits,
  logoutDating,
  isDatingAuthenticated
} from '../../services/dating/datingService';
import { useDatingNotifications, useDatingOnlineStatus } from '../../hooks/useDatingChat';

// ============================================================================
// CONTEXTE DATING
// ============================================================================

const DatingContext = createContext<DatingAuthContext | null>(null);

export const useDatingContext = () => {
  const context = useContext(DatingContext);
  if (!context) {
    throw new Error('useDatingContext must be used within DatingLayout');
  }
  return context;
};

// ============================================================================
// COMPOSANT LAYOUT
// ============================================================================

interface DatingLayoutProps {
  children?: React.ReactNode;
}

export const DatingLayout: React.FC<DatingLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [profile, setProfile] = useState<DatingProfile | null>(null);
  const [subscription, setSubscription] = useState<DatingSubscription | null>(null);
  const [dailyLimits, setDailyLimits] = useState<DatingDailyLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Charger les données utilisateur
  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);
      
      try {
        // Vérifier l'authentification
        if (!isDatingAuthenticated()) {
          // Rediriger vers la page de connexion sauf si déjà sur auth
          if (!location.pathname.includes('/dating/auth') && !location.pathname.includes('/dating/inscription')) {
            navigate('/dating/auth');
          }
          setIsLoading(false);
          return;
        }

        // Charger le profil
        const userProfile = await getCurrentDatingProfile();
        
        if (!userProfile) {
          // Pas de profil, rediriger vers l'inscription
          if (!location.pathname.includes('/dating/inscription')) {
            navigate('/dating/inscription');
          }
          setIsLoading(false);
          return;
        }

        setProfile(userProfile);

        // Charger l'abonnement actif
        const activeSub = await getActiveDatingSubscription(userProfile.id);
        setSubscription(activeSub);

        // Charger les limites quotidiennes
        const limits = await getDatingDailyLimits(userProfile.id);
        setDailyLimits(limits);

      } catch (error) {
        console.error('Erreur chargement données Dating:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [navigate, location.pathname]);

  // Hook pour le statut en ligne
  useDatingOnlineStatus({
    profileId: profile?.id || '',
    updateInterval: 30000
  });

  // Hook pour les notifications (seulement si profil chargé)
  const { unreadCount, conversations, refreshConversations } = useDatingNotifications({
    profileId: profile?.id || '', // Le hook vérifie maintenant si profileId est valide
    onNewMatch: (matchId) => {
      console.log('Nouveau match:', matchId);
    }
  });

  // Calculer les limites restantes
  const isVip = subscription?.is_active && subscription?.plan?.is_vip;
  const planLimits = subscription?.plan || { daily_likes_limit: 10, daily_super_likes_limit: 1, daily_messages_limit: 5 };
  
  const remainingLikes = planLimits.daily_likes_limit === null 
    ? Infinity 
    : (planLimits.daily_likes_limit || 10) - (dailyLimits?.likes_count || 0);
  
  const remainingSuperLikes = planLimits.daily_super_likes_limit === null
    ? Infinity
    : (planLimits.daily_super_likes_limit || 1) - (dailyLimits?.super_likes_count || 0);
  
  const remainingMessages = planLimits.daily_messages_limit === null
    ? Infinity
    : (planLimits.daily_messages_limit || 5) - (dailyLimits?.messages_count || 0);

  // Contexte
  const contextValue: DatingAuthContext = {
    profile,
    subscription,
    isAuthenticated: !!profile,
    isVip: !!isVip,
    dailyLimits,
    remainingLikes: Math.max(0, remainingLikes),
    remainingSuperLikes: Math.max(0, remainingSuperLikes),
    remainingMessages: Math.max(0, remainingMessages),
    // Données temps réel partagées
    unreadCount,
    conversations,
    refreshConversations
  };

  // Déconnexion
  const handleLogout = () => {
    logoutDating();
    setProfile(null);
    setSubscription(null);
    navigate('/dating/auth');
  };

  // Navigation items
  const navItems = [
    { path: '/dating', icon: Sparkles, label: 'Découvrir', exact: true },
    { path: '/dating/matches', icon: Heart, label: 'Matches' },
    { path: '/dating/messages', icon: MessageCircle, label: 'Messages', badge: unreadCount },
    { path: '/dating/profil', icon: User, label: 'Profil' }
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/80">Chargement...</p>
        </div>
      </div>
    );
  }

  // Pages publiques (auth, inscription)
  const isPublicPage = location.pathname.includes('/dating/auth') || 
                       location.pathname.includes('/dating/inscription') ||
                       location.pathname.includes('/dating/plans');

  if (isPublicPage) {
    return (
      <DatingContext.Provider value={contextValue}>
        <div className="min-h-screen bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-900">
          {children || <Outlet />}
        </div>
      </DatingContext.Provider>
    );
  }

  return (
    <DatingContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-900">
        {/* Header Desktop */}
        <header className="hidden md:flex fixed top-0 left-0 right-0 h-16 bg-black/30 backdrop-blur-lg border-b border-white/10 z-50">
          <div className="container mx-auto px-4 flex items-center justify-between">
            {/* Logo */}
            <Link to="/dating" className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-pink-500 fill-pink-500" />
              <span className="text-xl font-bold text-white">Dating</span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = item.exact 
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-pink-500/20 text-pink-400' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 text-white text-xs flex items-center justify-center">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Badge VIP */}
              {isVip ? (
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-sm font-semibold">
                  <Crown className="w-4 h-4" />
                  VIP
                </div>
              ) : (
                <Link
                  to="/dating/plans"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  <Crown className="w-4 h-4" />
                  Devenir VIP
                </Link>
              )}

              {/* Profil dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-2 p-1 rounded-full hover:bg-white/10 transition-colors">
                  <img
                    src={profile?.avatar_url || '/placeholder-avatar.png'}
                    alt={profile?.display_name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-pink-500"
                  />
                </button>
                
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 w-56 py-2 bg-gradient-to-b from-purple-900/95 to-purple-950/95 backdrop-blur-xl rounded-2xl border border-pink-500/30 shadow-2xl shadow-purple-900/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {/* Header du dropdown */}
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-white font-semibold truncate">{profile?.display_name}</p>
                    <p className="text-white/50 text-sm truncate">{profile?.email}</p>
                  </div>
                  
                  <div className="py-2">
                    <Link
                      to="/dating/profil"
                      className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Mon profil
                    </Link>
                    <Link
                      to="/dating/parametres"
                      className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Paramètres
                    </Link>
                    <Link
                      to="/dating/abonnement"
                      className="flex items-center gap-3 px-4 py-2.5 text-amber-400 hover:bg-amber-500/10 transition-colors"
                    >
                      <Crown className="w-4 h-4" />
                      Mon abonnement
                    </Link>
                  </div>
                  
                  <div className="border-t border-white/10 pt-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-500/10 w-full text-left transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Header Mobile */}
        <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-black/30 backdrop-blur-lg border-b border-white/10 z-50">
          <div className="flex items-center justify-between h-full px-4">
            <Link to="/dating" className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
              <span className="text-lg font-bold text-white">Dating</span>
            </Link>

            <div className="flex items-center gap-2">
              {!isVip && (
                <Link
                  to="/dating/plans"
                  className="p-2 rounded-full bg-gradient-to-r from-pink-500 to-rose-500"
                >
                  <Crown className="w-5 h-5 text-white" />
                </Link>
              )}
              
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 rounded-full hover:bg-white/10"
              >
                <Menu className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25 }}
                className="absolute right-0 top-0 bottom-0 w-72 bg-gray-900 border-l border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <img
                      src={profile?.avatar_url || '/placeholder-avatar.png'}
                      alt={profile?.display_name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-pink-500"
                    />
                    <div>
                      <p className="font-semibold text-white">{profile?.display_name}</p>
                      {isVip && (
                        <span className="text-xs text-yellow-500 flex items-center gap-1">
                          <Crown className="w-3 h-3" /> VIP
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-full hover:bg-white/10"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-2">
                  {navItems.map((item) => {
                    const isActive = item.exact 
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive 
                            ? 'bg-pink-500/20 text-pink-400' 
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <span className="ml-auto w-6 h-6 rounded-full bg-pink-500 text-white text-xs flex items-center justify-center">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>

                <hr className="mx-4 border-white/10" />

                {/* Actions */}
                <div className="p-4 space-y-2">
                  <Link
                    to="/dating/parametres"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/10 hover:text-white"
                  >
                    <Settings className="w-5 h-5" />
                    Paramètres
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    Déconnexion
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Mobile (Bottom) */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-black/30 backdrop-blur-lg border-t border-white/10 z-40">
          <div className="flex items-center justify-around h-full">
            {navItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex flex-col items-center gap-1 p-2 ${
                    isActive ? 'text-pink-400' : 'text-white/50'
                  }`}
                >
                  <item.icon className="w-6 h-6" />
                  <span className="text-xs">{item.label}</span>
                  {typeof item.badge === 'number' && item.badge > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-pink-500 text-white text-[10px] flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Contenu principal */}
        <main className="pt-16 md:pt-16 pb-20 md:pb-0 min-h-screen">
          {children || <Outlet />}
        </main>
      </div>
    </DatingContext.Provider>
  );
};

export default DatingLayout;
